import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  FaMobileAlt,
  FaCheckCircle,
  FaClock,
  FaSchool,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaSyncAlt,
  FaCopy,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  getAssignedDevices,
  getAssignedDevicesBySchool,
  addMultipleDevices,
  editAssignedDevice,
  deleteAssignedDevice,
} from "../../services/deviceAssignService";
import { getSchools } from "../../services/schoolService";
import "./AssignDeviceLayer.css";

const initialAddForm = {
  school_id: "",
  devicesList: [""],
  isBulkMode: false,
  bulkText: "",
  label: "",
};

const initialEditForm = {
  id: null,
  mac_id: "",
  devicesList: [""],
  school_id: "",
  label: "",
  status: "assigned",
};

const AssignDeviceLayer = () => {
  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState({
    total_devices: 0,
    total_assigned: 0,
    total_pending: 0,
  });
  const [schoolBreakdown, setSchoolBreakdown] = useState([]);
  const [schoolsList, setSchoolsList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters & Pagination
  const [search, setSearch] = useState("");
  const [selectedSchoolFilter, setSelectedSchoolFilter] = useState("all");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [addFormData, setAddFormData] = useState(initialAddForm);
  const [editFormData, setEditFormData] = useState(initialEditForm);
  const [submitting, setSubmitting] = useState(false);

  /* ================= FETCH DATA ================= */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Load assigned devices
      let res;
      if (selectedSchoolFilter !== "all" && selectedSchoolFilter !== "") {
        res = await getAssignedDevicesBySchool(selectedSchoolFilter);
      } else {
        res = await getAssignedDevices();
      }

      if (res?.status === true || res?.action) {
        setDevices(res.data || []);
        if (res.summary) {
          setSummary(res.summary);
        } else {
          // Calculate summary fallback if not provided
          const allData = res.data || [];
          const assignedCount = allData.filter(
            (d) => d.status === "assigned"
          ).length;
          const pendingCount = allData.filter(
            (d) => d.status === "pending"
          ).length;
          setSummary({
            total_devices: allData.length,
            total_assigned: assignedCount,
            total_pending: pendingCount,
          });
        }

        if (res.school_assigned_breakdown) {
          setSchoolBreakdown(res.school_assigned_breakdown);
        }
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error("Failed to fetch device data:", error);
      toast.error("Failed to fetch device assignments");
    } finally {
      setLoading(false);
    }
  }, [selectedSchoolFilter]);

  const fetchSchoolsDropdown = async () => {
    try {
      const res = await getSchools();
      if (res?.status && Array.isArray(res.data)) {
        setSchoolsList(res.data);
      } else if (Array.isArray(res?.data)) {
        setSchoolsList(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch schools list:", error);
    }
  };

  useEffect(() => {
    fetchSchoolsDropdown();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ================= COPY TO CLIPBOARD ================= */
  const handleCopy = (macId) => {
    navigator.clipboard.writeText(macId);
    toast.success(`Copied MAC ID: ${macId}`);
  };

  /* ================= ADD DYNAMIC DEVICE HANDLERS ================= */
  const handleAddDeviceField = () => {
    setAddFormData((prev) => ({
      ...prev,
      devicesList: [...prev.devicesList, ""],
    }));
  };

  const handleRemoveAddDeviceField = (index) => {
    setAddFormData((prev) => ({
      ...prev,
      devicesList: prev.devicesList.filter((_, i) => i !== index),
    }));
  };

  const handleAddDeviceChange = (index, value) => {
    const updated = [...addFormData.devicesList];
    updated[index] = value;
    setAddFormData({ ...addFormData, devicesList: updated });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addFormData.school_id) {
      return toast.warning("Please select a school");
    }

    let deviceList = [];
    if (addFormData.isBulkMode) {
      deviceList = addFormData.bulkText
        .split(/[\n,]+/)
        .map((d) => d.trim())
        .filter(Boolean);
    } else {
      deviceList = addFormData.devicesList
        .map((d) => d.trim())
        .filter(Boolean);
    }

    if (deviceList.length === 0) {
      return toast.warning("Please enter at least one valid Device / MAC ID");
    }

    setSubmitting(true);
    try {
      const res = await addMultipleDevices({
        school_id: addFormData.school_id,
        devices: deviceList,
        label: addFormData.label,
      });

      const isSuccess =
        res?.status === true ||
        res?.status === "success" ||
        res?.status === 1 ||
        res?.success === true ||
        res?.success === "true" ||
        res?.code === 200;

      if (isSuccess) {
        toast.success(
          res.message || `${deviceList.length} device(s) assigned successfully!`
        );
        setShowAddModal(false);
        setAddFormData(initialAddForm);
        await fetchData();
      } else {
        toast.error(res?.message || "Failed to assign devices");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while adding devices");
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= EDIT DYNAMIC DEVICE HANDLERS ================= */
  const openEditModal = (device) => {
    // Gather all MAC IDs currently assigned to this school
    let schoolMacs = [];
    if (device.school_id) {
      schoolMacs = devices
        .filter((d) => String(d.school_id) === String(device.school_id))
        .map((d) => d.mac_id)
        .filter(Boolean);
    }

    // Ensure the current device's MAC ID is placed at the top
    if (device.mac_id) {
      schoolMacs = [
        device.mac_id,
        ...schoolMacs.filter((m) => m !== device.mac_id),
      ];
    }

    if (schoolMacs.length === 0) {
      schoolMacs = [""];
    }

    setEditFormData({
      id: device.id,
      mac_id: device.mac_id || "",
      devicesList: schoolMacs,
      school_id: device.school_id || "",
      label: device.label || "",
      status: device.status || "assigned",
    });
    setShowEditModal(true);
  };

  const handleEditDeviceField = () => {
    setEditFormData((prev) => ({
      ...prev,
      devicesList: [...prev.devicesList, ""],
    }));
  };

  const handleRemoveEditDeviceField = (index) => {
    setEditFormData((prev) => ({
      ...prev,
      devicesList: prev.devicesList.filter((_, i) => i !== index),
    }));
  };

  const handleEditDeviceChange = (index, value) => {
    const updated = [...editFormData.devicesList];
    updated[index] = value;
    setEditFormData({ ...editFormData, devicesList: updated });
  };

  const handleEditSchoolChange = (newSchoolId) => {
    let schoolMacs = [];
    if (newSchoolId) {
      schoolMacs = devices
        .filter((d) => String(d.school_id) === String(newSchoolId))
        .map((d) => d.mac_id)
        .filter(Boolean);
    }

    if (schoolMacs.length === 0) {
      schoolMacs =
        editFormData.devicesList.length > 0 ? editFormData.devicesList : [""];
    }

    setEditFormData((prev) => ({
      ...prev,
      school_id: newSchoolId,
      devicesList: schoolMacs,
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const validDevices = editFormData.devicesList
      .map((d) => d.trim())
      .filter(Boolean);

    const primaryMac = validDevices[0];
    if (!primaryMac) {
      return toast.warning("Primary MAC ID is required");
    }

    setSubmitting(true);
    try {
      // 1. Update primary device assignment
      const res = await editAssignedDevice({
        id: editFormData.id,
        mac_id: primaryMac,
        school_id: editFormData.school_id,
        label: editFormData.label,
        status: editFormData.status,
      });

      const isSuccess =
        res?.status === true ||
        res?.status === "success" ||
        res?.status === 1 ||
        res?.success === true ||
        res?.success === "true" ||
        res?.code === 200;

      if (!isSuccess) {
        toast.error(res?.message || "Failed to update device assignment");
        setSubmitting(false);
        return;
      }

      // 2. If multiple devices belong to this school, sync/add them as well
      if (validDevices.length > 1 && editFormData.school_id) {
        await addMultipleDevices({
          school_id: editFormData.school_id,
          devices: validDevices,
          label: editFormData.label,
        });
      }

      toast.success(res?.message || "Device assignment updated successfully!");
      setShowEditModal(false);
      setEditFormData(initialEditForm);
      await fetchData();
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while updating device");
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= DELETE DEVICE ================= */
  const handleDelete = async (id, macId) => {
    if (
      !window.confirm(
        `Are you sure you want to delete assignment for MAC ID: ${macId || id}?`
      )
    ) {
      return;
    }

    try {
      const res = await deleteAssignedDevice(id);
      if (res?.status === true || res?.success) {
        toast.success("Device assignment deleted successfully");
        fetchData();
      } else {
        toast.error(res?.message || "Failed to delete assignment");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while deleting");
    }
  };

  /* ================= SEARCH & FILTERING ================= */
  const filteredDevices = useMemo(() => {
    return devices.filter((item) => {
      const macMatch = item.mac_id
        ? item.mac_id.toLowerCase().includes(search.toLowerCase())
        : false;
      const labelMatch = item.label
        ? item.label.toLowerCase().includes(search.toLowerCase())
        : false;
      const schoolMatch = item.school_name
        ? item.school_name.toLowerCase().includes(search.toLowerCase())
        : false;
      const textMatch = search === "" || macMatch || labelMatch || schoolMatch;

      const statusMatch =
        selectedStatusFilter === "all" || item.status === selectedStatusFilter;

      return textMatch && statusMatch;
    });
  }, [devices, search, selectedStatusFilter]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredDevices.length / pageSize) || 1;
  const displayedDevices = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredDevices.slice(start, start + pageSize);
  }, [filteredDevices, page, pageSize]);

  return (
    <div className="device-wrapper">
      {/* SUMMARY WIDGET CARDS */}
      <div className="device-stats-grid">
        <div className="stat-card">
          <div className="stat-icon-wrapper stat-icon-blue">
            <FaMobileAlt />
          </div>
          <div className="stat-info">
            <h6>Total Devices</h6>
            <h3>{summary.total_devices || devices.length}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper stat-icon-green">
            <FaCheckCircle />
          </div>
          <div className="stat-info">
            <h6>Assigned Devices</h6>
            <h3>
              {summary.total_assigned !== undefined
                ? summary.total_assigned
                : devices.filter((d) => d.status === "assigned").length}
            </h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper stat-icon-orange">
            <FaClock />
          </div>
          <div className="stat-info">
            <h6>Pending Devices</h6>
            <h3>
              {summary.total_pending !== undefined
                ? summary.total_pending
                : devices.filter((d) => d.status === "pending").length}
            </h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper stat-icon-purple">
            <FaSchool />
          </div>
          <div className="stat-info">
            <h6>Schools Assigned</h6>
            <h3>{schoolBreakdown.length || schoolsList.length}</h3>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT CARD */}
      <div className="device-card">
        {/* HEADER */}
        <div className="device-list-header">
          <h5>
            <FaMobileAlt className="text-primary" /> Device Management &
            Assignments
          </h5>
          <div className="header-actions">
            <button
              className="btn-refresh"
              onClick={fetchData}
              title="Refresh Data"
            >
              <FaSyncAlt className={loading ? "spin" : ""} /> Refresh
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                setAddFormData(initialAddForm);
                setShowAddModal(true);
              }}
            >
              <FaPlus /> Assign Devices (Bulk)
            </button>
          </div>
        </div>

        {/* CONTROLS & FILTERS */}
        <div className="device-controls">
          <div className="filter-group">
            <div className="device-search-box">
              <FaSearch className="text-slate-400" />
              <input
                type="text"
                placeholder="Search MAC ID, Label, or School..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            {/* School Filter Dropdown */}
            <select
              className="select-filter"
              value={selectedSchoolFilter}
              onChange={(e) => {
                setSelectedSchoolFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All Schools</option>
              {schoolsList.map((sch) => (
                <option key={sch.id} value={sch.id}>
                  {sch.name} (ID: {sch.id})
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              className="select-filter"
              value={selectedStatusFilter}
              onChange={(e) => {
                setSelectedStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All Statuses</option>
              <option value="assigned">Assigned</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="page-size">
            <span>Show:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="user-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>MAC ID / Device</th>
                <th>School Name</th>
                <th>Label / Location</th>
                <th>Status</th>
                <th>Last Seen</th>
                <th>Created At</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    Loading device assignments...
                  </td>
                </tr>
              ) : displayedDevices.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    No device assignments found.
                  </td>
                </tr>
              ) : (
                displayedDevices.map((item) => (
                  <tr key={item.id}>
                    <td>#{item.id}</td>
                    <td>
                      <span className="mac-badge">
                        {item.mac_id}
                        <button
                          className="copy-btn"
                          onClick={() => handleCopy(item.mac_id)}
                          title="Copy MAC ID"
                        >
                          <FaCopy />
                        </button>
                      </span>
                    </td>
                    <td>
                      {item.school_name ? (
                        <span className="school-tag">{item.school_name}</span>
                      ) : (
                        <span className="text-muted">Unassigned</span>
                      )}
                    </td>
                    <td>{item.label || <span className="text-muted">-</span>}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          item.status === "assigned"
                            ? "status-assigned"
                            : "status-pending"
                        }`}
                      >
                        {item.status === "assigned" ? (
                          <FaCheckCircle />
                        ) : (
                          <FaClock />
                        )}
                        {item.status || "pending"}
                      </span>
                    </td>
                    <td>
                      {item.last_seen_at ? (
                        new Date(item.last_seen_at).toLocaleString()
                      ) : (
                        <span className="text-muted">Never</span>
                      )}
                    </td>
                    <td>
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <div className="actions" style={{ justifyContent: "flex-end" }}>
                        <button
                          onClick={() => openEditModal(item)}
                          className="text-primary-600"
                          title="Edit Assignment"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id, item.mac_id)}
                          className="text-danger-600"
                          title="Delete Assignment"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {!loading && filteredDevices.length > 0 && (
          <div className="pagination">
            <span>
              Showing {(page - 1) * pageSize + 1} to{" "}
              {Math.min(page * pageSize, filteredDevices.length)} of{" "}
              {filteredDevices.length} entries
            </span>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                disabled={page === 1}
                onClick={() => setPage((prev) => prev - 1)}
              >
                <FaChevronLeft /> Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL: ADD MULTIPLE DEVICES */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h5>Assign Devices to School</h5>
              <button
                className="modal-close-btn"
                onClick={() => setShowAddModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Select School *</label>
                  <select
                    required
                    value={addFormData.school_id}
                    onChange={(e) =>
                      setAddFormData({ ...addFormData, school_id: e.target.value })
                    }
                  >
                    <option value="">-- Choose School --</option>
                    {schoolsList.map((sch) => (
                      <option key={sch.id} value={sch.id}>
                        {sch.name} (ID: {sch.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <div className="input-mode-toggle">
                    <label style={{ margin: 0 }}>Device MAC IDs *</label>
                    <button
                      type="button"
                      className="btn-link-toggle"
                      onClick={() =>
                        setAddFormData((prev) => ({
                          ...prev,
                          isBulkMode: !prev.isBulkMode,
                        }))
                      }
                    >
                      {addFormData.isBulkMode
                        ? "Switch to Dynamic Inputs"
                        : "Switch to Bulk Paste Text Area"}
                    </button>
                  </div>

                  {addFormData.isBulkMode ? (
                    <>
                      <textarea
                        rows={4}
                        required
                        placeholder="Enter MAC IDs separated by commas or line breaks&#10;e.g., 123123123133, 987654321022"
                        value={addFormData.bulkText}
                        onChange={(e) =>
                          setAddFormData({
                            ...addFormData,
                            bulkText: e.target.value,
                          })
                        }
                      ></textarea>
                      <div className="form-hint">
                        Enter one or multiple MAC addresses separated by lines or commas.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="device-input-group">
                        {addFormData.devicesList.map((macVal, index) => (
                          <div key={index} className="device-input-row">
                            <input
                              type="text"
                              required={index === 0}
                              placeholder={`Enter MAC ID #${index + 1}`}
                              value={macVal}
                              onChange={(e) =>
                                handleAddDeviceChange(index, e.target.value)
                              }
                            />
                            {addFormData.devicesList.length > 1 && (
                              <button
                                type="button"
                                className="btn-remove-row"
                                onClick={() => handleRemoveAddDeviceField(index)}
                                title="Remove Field"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn-add-row"
                        onClick={handleAddDeviceField}
                      >
                        <FaPlus /> Add Device / MAC ID
                      </button>
                    </>
                  )}
                </div>

                <div className="form-group">
                  <label>Label / Location Name</label>
                  <input
                    type="text"
                    placeholder="e.g., Main Entrance Gate"
                    value={addFormData.label}
                    onChange={(e) =>
                      setAddFormData({ ...addFormData, label: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Assigning..." : "Assign Devices"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: EDIT DEVICE */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h5>Edit Device Assignment #{editFormData.id}</h5>
              <button
                className="modal-close-btn"
                onClick={() => setShowEditModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Device MAC ID(s) *</label>
                  <div className="device-input-group">
                    {editFormData.devicesList.map((macVal, index) => (
                      <div key={index} className="device-input-row">
                        <input
                          type="text"
                          required={index === 0}
                          placeholder={
                            index === 0
                              ? "Edit Primary MAC ID *"
                              : `Add New MAC ID #${index + 1}`
                          }
                          value={macVal}
                          onChange={(e) =>
                            handleEditDeviceChange(index, e.target.value)
                          }
                        />
                        {editFormData.devicesList.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove-row"
                            onClick={() => handleRemoveEditDeviceField(index)}
                            title="Remove Field"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn-add-row"
                    onClick={handleEditDeviceField}
                  >
                    <FaPlus /> Add Device / MAC ID
                  </button>
                  <div className="form-hint">
                    You can edit the existing MAC ID or add new devices to this school.
                  </div>
                </div>

                <div className="form-group">
                  <label>Assign to School</label>
                  <select
                    value={editFormData.school_id || ""}
                    onChange={(e) => handleEditSchoolChange(e.target.value)}
                  >
                    <option value="">-- Unassigned / Select School --</option>
                    {schoolsList.map((sch) => (
                      <option key={sch.id} value={sch.id}>
                        {sch.name} (ID: {sch.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Label / Location</label>
                  <input
                    type="text"
                    placeholder="e.g., Updated Main Gate Reader"
                    value={editFormData.label}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, label: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, status: e.target.value })
                    }
                  >
                    <option value="assigned">assigned</option>
                    <option value="pending">pending</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignDeviceLayer;
